import React from "react";

import { css } from "@emotion/css";
import Alert from "@mui/material/Alert";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { useTranslation } from "react-i18next";

import { useStyles } from "@/hooks/useStyles.ts";
import { AlertConfig, AlertType } from "@/models/AlertModel.ts";
import { AlertSettingsRow } from "@/pages/patient-settings/alert-settings/AlertSettingsRow.tsx";
import { Theme } from "@/theme/CreateTheme.ts";
import { Typography } from "@/uiKit/atoms/Typography.tsx";

const TYPE_COL_MAX_WIDTH_PX = 200;
const TYPE_COL_MIN_WIDTH_PX = 100;
const CONDITIONS_COL_MIN_WIDTH_PX = 200;
const ACTION_COL_WIDTH_PX = 50;

export type AlertSettingsTableProps = {
  patientId: string;
  alertConfigList: AlertConfig[];
  alertType: AlertType;
};

const getAlertTranslationName = (alertType: AlertType) => {
  switch (alertType) {
    case "bgm":
      return "alertConfig.noAlertBGM";
    case "cgm":
      return "alertConfig.noAlertCGM";
    case "other":
      return "alertConfig.noAlertOther";
  }
};

export const AlertSettingsTable: React.FC<AlertSettingsTableProps> = ({ patientId, alertConfigList, alertType }) => {
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();

  const alerts = alertConfigList.filter(alertConfig => alertConfig.type === alertType);

  return (
    <div className={styles.container}>
      <table className={styles.table}>
        <TableHead className={styles.tableHeader}>
          <TableRow className={styles.row}>
            <TableCell className={styles.typeColumn}>
              <Typography variant="h6">{t("alertConfig.type")}</Typography>
            </TableCell>
            <TableCell className={styles.conditionsColumn}>
              <Typography variant="h6">{t("alertConfig.conditions")}</Typography>
            </TableCell>
            <TableCell className={styles.actionColumn}></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {alerts.length > 0 ? (
            alerts.map(alertConfig => (
              <AlertSettingsRow
                key={alertConfig.id}
                patientId={patientId}
                alertConfig={alertConfig}
                className={styles.row}
                classes={{
                  typeCol: styles.typeColumn,
                  conditionsCol: styles.conditionsColumn,
                  actionCol: styles.actionColumn,
                }}
              />
            ))
          ) : (
            <TableRow>
              <TableCell>
                <Alert severity="info" className={styles.margin}>
                  <Typography>{t(getAlertTranslationName(alertType))}</Typography>
                </Alert>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </table>
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css``,
  table: css`
    width: 100%;
  `,
  tableHeader: css`
    width: 100%;
  `,
  row: css`
    display: flex;
    width: 100%;
  `,
  typeColumn: css`
    max-width: ${TYPE_COL_MAX_WIDTH_PX}px;
    min-width: ${TYPE_COL_MIN_WIDTH_PX}px;
    flex: 1 1 ${TYPE_COL_MAX_WIDTH_PX}px;
    display: flex;
    align-items: center;
  `,
  conditionsColumn: css`
    min-width: ${CONDITIONS_COL_MIN_WIDTH_PX}px;
    flex: 3 1;
    display: flex;
    align-items: center;
  `,
  actionColumn: css`
    min-width: ${ACTION_COL_WIDTH_PX}px;
    flex: 0 0 ${ACTION_COL_WIDTH_PX}px;
    display: flex;
    align-items: center;
  `,
  margin: css`
    margin: ${theme.spacing(8)};
  `,
});
